import { Injectable } from '@angular/core';
import { SortDirection } from '@ddt-web/common';
import { CellData, ComponentData, TabData, TableData, TransformedComponentData } from '@ddt-web/data-service';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { ComponentDataTransformerSortingOptions } from '../models/component-data-transformer-sorting-options.interface';

/**
 * This class takes componentData and converts it into a JSON object in the format TransformedComponentData.
 *
 * 	componentId: {
 * 		tabId: {
 * 			tableData: {
 * 				cells: {
 * 					...
 * 					cellKeys: cellValue
 * 					...
 * 				}
 * 			}
 * 		}
 * }
 *
 * Currently only used for excel.
 */
@Injectable()
export class ComponentDataTransformerService {

	public transformComponentData(
		componentDataArray: Array<ComponentData>,
		sortOptions: ComponentDataTransformerSortingOptions = { sortKey: 'ticker', sortOrder: SortDirection.ASC },
		filterInvisibleTabs: boolean = false
	): TransformedComponentData {
		if (!componentDataArray) {
			return {};
		}

		return componentDataArray.reduce((componentDataMap, componentData) => {
			componentDataMap[componentData.id] = get(componentData, 'tabs', [])
				.filter((tab: TabData) => !filterInvisibleTabs || filterInvisibleTabs && tab.displayConfig.visible)
				.reduce((tabs, tab: TabData) => {
					tabs[tab.id] = {
						tableData: get(tab, 'tableData', []).map((tableData: TableData) => {
							let dataToTransform;
							if (get(sortOptions, 'sortKey') && !isNil(get(sortOptions, 'sortOrder'))) {
								dataToTransform = this.sortTableData(tableData, sortOptions.sortKey, sortOptions.sortOrder);
							} else {
								dataToTransform = tableData;
							}

							return {
								cells: get(dataToTransform, 'cells', []).map((cellRow: CellData[]) => cellRow.reduce((simpleCells, cellData: CellData) => {
									if (get(cellData, 'config.label.attributes.useFormattedValueForExcel')) {
										simpleCells[cellData.config.id] = cellData.formattedValue;
									} else {
										simpleCells[cellData.config.id] = cellData.rawValue;
									}
									return simpleCells;
								}, {}))
							};
						})
					};
					return tabs;
				}, {});
			return componentDataMap;
		}, {});
	}

	/** NOTE: This is needed temporarily due to many circular dependencies that are very complicated to fix */

	/**
	 * Sorts the provided table data object using the provided sort key and sort direction and returns the result.
	 * If sortDirection is 0 or table data is null or undefined or table data has no cells, the provided table data is returned.
	 *
	 * @param tableData table data object to be sorted
	 * @param sortKey the key used to sort the table data (id of the column to be sorted)
	 * @param sortDirection the direction of sorting (0 unsorted, 1 ascending, -1 descending)
	 * @returns the sorted table data
	 */
	public sortTableData(tableData: TableData, sortKey: string, sortDirection: number): TableData {
		if (sortDirection === SortDirection.UNSORTED || isNil(tableData) || isNil(tableData.cells)) {
			return tableData;
		}

		const cellDataArray: CellData[][] = tableData.cells;
		cellDataArray.sort((cellData1: CellData[], cellData2: CellData[]) => {
			const cellData1ToSort: CellData = cellData1.find(cell => cell.config.id === sortKey);
			const cellData2ToSort: CellData = cellData2.find(cell => cell.config.id === sortKey);

			return this.sortCellData(cellData1ToSort, cellData2ToSort, sortDirection);
		});

		return new TableData(
			tableData.headersX,
			tableData.headersY,
			cellDataArray,
			tableData.portfolioData,
			tableData.firstColumnTitle,
			tableData.transposed
		);
	}

	/**
	 * Sorts the provided 2 cell data objects using the provided sort direction and returns the result.
	 *
	 * @param cellData1 CellData object
	 * @param cellData2 CellData object
	 * @param sortDirection the direction of sorting (0 unsorted, 1 ascending, -1 descending)
	 * @returns number
	 */
	public sortCellData(cellData1: CellData, cellData2: CellData, sortDirection: SortDirection): number {
		const rawVal1 = get(cellData1, 'rawValue') as any;
		const rawVal2 = get(cellData2, 'rawValue') as any;
		const formattedVal1 = get(cellData1, 'formattedValue') as any;
		const formattedVal2 = get(cellData2, 'formattedValue') as any;

		if (formattedVal1 === 'n/a' && formattedVal2 === 'n/a') {
			return 0;
		}

		if (isNil(rawVal1) && !isNil(rawVal2)) {
			return 1;
		}

		if (!isNil(rawVal1) && isNil(rawVal2)) {
			return -1;
		}

		if (typeof rawVal1 === 'string' || typeof rawVal2 === 'string') {
			return sortDirection === SortDirection.ASC ? rawVal1.toString().localeCompare(rawVal2.toString()) : rawVal2.toString().localeCompare(rawVal1.toString());
		}

		return sortDirection === SortDirection.ASC ? rawVal1 - rawVal2 : rawVal2 - rawVal1;
	}
}
